import { type ChangeEvent, type KeyboardEvent, useEffect, useMemo, useState } from 'react';

import {
  IconArrowsExchange,
  IconCirclePlus,
  IconCode,
  IconEye,
  IconTable,
  IconTrash,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';

import {
  type PolarExportFormat,
  type PolarPoint,
  convertPolarDocument,
  getPolarDocumentFormat,
  parsePolarDocument,
  serializePolarDocument,
  summarizePolarPoints,
} from '../../utils/polarDocument';

type PolarVisualEditorProperties = {
  value: string;
  disabled?: boolean;
  onChange: (nextValue: string) => void;
};

type ChartRow = {
  angle: number;
  [series: string]: number | string | null;
};

type PolarPointField = 'tws' | 'twa' | 'speed';

const chartColors = ['#50aece', '#8bd17c', '#f4b860', '#ef7b6d', '#a78bfa', '#5eead4', '#f472b6'];
const CERTIFIED_JSON_FORMAT: PolarExportFormat = 'certified-json';
const TABLE_SEMICOLON_FORMAT: PolarExportFormat = 'table-semicolon';
const TABLE_SPACE_FORMAT: PolarExportFormat = 'table-space';

const formatNumberInput = (value: number): string => Number.parseFloat(value.toFixed(4)).toString();

const nextPointId = (): string => `polar-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

const makeSeriesKey = (tws: number): string => `tws_${String(tws).replaceAll('.', '_')}`;

const parseEditorNumber = (value: string): number | null => {
  const normalized = value.trim().replace(',', '.');
  if (normalized.length === 0) {
    return null;
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const pointDraftKey = (pointId: string, field: PolarPointField): string => `${pointId}:${field}`;

const finishEditOnEnter = (event: KeyboardEvent<HTMLInputElement>) => {
  if (event.key === 'Enter') {
    event.currentTarget.blur();
  }
};

export const PolarVisualEditor = ({ value, disabled = false, onChange }: PolarVisualEditorProperties) => {
  const { t } = useTranslation('common');
  const document = useMemo(() => parsePolarDocument(value), [value]);
  const [selectedTws, setSelectedTws] = useState<number | null>(null);
  const [selectedChartTws, setSelectedChartTws] = useState<number[]>([]);
  const [showRaw, setShowRaw] = useState(false);
  const [targetFormat, setTargetFormat] = useState<PolarExportFormat>(CERTIFIED_JSON_FORMAT);
  const [pointDrafts, setPointDrafts] = useState<Record<string, string>>({});

  const sortedPoints = useMemo(
    () => [...document.points].sort((left, right) => left.twa - right.twa || left.tws - right.tws),
    [document.points],
  );
  const summary = useMemo(() => summarizePolarPoints(sortedPoints), [sortedPoints]);
  const twsValues = useMemo(
    () => [...new Set(sortedPoints.map((point) => point.tws))].sort((a, b) => a - b),
    [sortedPoints],
  );
  const activeChartTws = useMemo(
    () => selectedChartTws.filter((tws) => twsValues.includes(tws)),
    [selectedChartTws, twsValues],
  );
  const visibleTws = selectedTws ?? activeChartTws[0] ?? twsValues[0] ?? null;
  const visiblePoints =
    activeChartTws.length === 0 ? [] : sortedPoints.filter((point) => activeChartTws.includes(point.tws));
  const currentFormat = getPolarDocumentFormat(document);
  const canTransform = document.valid && sortedPoints.length > 0 && !disabled;

  useEffect(() => {
    if (selectedTws !== null && !twsValues.includes(selectedTws)) {
      setSelectedTws(activeChartTws[0] ?? twsValues[0] ?? null);
    }
  }, [activeChartTws, selectedTws, twsValues]);

  useEffect(() => {
    setSelectedChartTws((current) => {
      const kept = current.filter((tws) => twsValues.includes(tws));
      if (kept.length > 0 || twsValues.length === 0) {
        return kept;
      }
      return [twsValues[0]];
    });
  }, [twsValues]);

  const chartRows = useMemo<ChartRow[]>(() => {
    const angles = [...new Set(sortedPoints.map((point) => point.twa))].sort((a, b) => a - b);
    return angles.map((angle) => {
      const row: ChartRow = { angle };
      for (const tws of twsValues) {
        row[makeSeriesKey(tws)] =
          sortedPoints.find((point) => point.twa === angle && point.tws === tws)?.speed ?? null;
      }
      return row;
    });
  }, [sortedPoints, twsValues]);

  const commitPoints = (points: PolarPoint[]) => {
    onChange(serializePolarDocument(document, points));
  };

  const updatePointDraft = (id: string, field: PolarPointField, rawValue: string) => {
    const key = pointDraftKey(id, field);
    setPointDrafts((current) => ({ ...current, [key]: rawValue }));

    // TWS and TWA are part of the parsed point identity. Committing either one
    // while typing would remount and move the row before the edit is finished.
    if (field !== 'speed') {
      return;
    }

    const parsed = parseEditorNumber(rawValue);
    if (parsed === null || parsed <= 0) {
      return;
    }
    commitPoints(
      sortedPoints.map((point) => (point.id === id ? { ...point, [field]: parsed } : point)),
    );
  };

  const finishPointEdit = (point: PolarPoint, field: PolarPointField, rawValue: string) => {
    const key = pointDraftKey(point.id, field);
    setPointDrafts((current) => {
      if (!(key in current)) {
        return current;
      }
      const next = { ...current };
      delete next[key];
      return next;
    });

    if (field === 'speed') {
      return;
    }

    const parsed = parseEditorNumber(rawValue);
    if (parsed === null || parsed === point[field]) {
      return;
    }

    commitPoints(
      sortedPoints.map((candidate) =>
        candidate.id === point.id ? { ...candidate, [field]: parsed } : candidate,
      ),
    );
  };

  const addPoint = () => {
    const seedTws = visibleTws ?? twsValues[0] ?? 6;
    const lastForTws = [...sortedPoints].reverse().find((point) => point.tws === seedTws);
    const nextTwa = Math.min(180, (lastForTws?.twa ?? 30) + 5);
    const nextSpeed = lastForTws?.speed ?? 0;
    commitPoints([
      ...sortedPoints,
      {
        id: nextPointId(),
        tws: seedTws,
        twa: nextTwa,
        speed: nextSpeed,
      },
    ]);
    setSelectedTws(seedTws);
  };

  const removePoint = (id: string) => {
    const keyPrefix = `${id}:`;
    setPointDrafts((current) =>
      Object.fromEntries(Object.entries(current).filter(([key]) => !key.startsWith(keyPrefix))),
    );
    commitPoints(sortedPoints.filter((point) => point.id !== id));
  };

  const toggleChartTws = (tws: number) => {
    const isSelected = activeChartTws.includes(tws);
    const next = isSelected
      ? activeChartTws.filter((value) => value !== tws)
      : [...activeChartTws, tws].sort((left, right) => left - right);
    if (next.length === 0) {
      return;
    }
    let nextTableTws = selectedTws;
    if (isSelected && selectedTws === tws) {
      nextTableTws = next[0] ?? null;
    }
    if (!isSelected) {
      nextTableTws = tws;
    }
    setSelectedChartTws(next);
    setSelectedTws(nextTableTws);
  };

  const updateRaw = (event: ChangeEvent<HTMLTextAreaElement>) => {
    onChange(event.target.value);
  };

  const transformPolars = () => {
    if (!canTransform) {
      return;
    }
    onChange(convertPolarDocument(document, targetFormat, sortedPoints));
    setShowRaw(true);
  };

  const formatLabel = (format: PolarExportFormat | 'empty' | 'invalid-json'): string => {
    if (format === CERTIFIED_JSON_FORMAT) {
      return t('settings.boats.formatJson', 'Certified JSON');
    }
    if (format === TABLE_SEMICOLON_FORMAT) {
      return t('settings.boats.formatSemicolonTable', 'Semicolon table');
    }
    if (format === TABLE_SPACE_FORMAT) {
      return t('settings.boats.formatSpaceTable', 'Space table');
    }
    if (format === 'invalid-json') {
      return t('settings.boats.formatInvalidJson', 'Invalid JSON');
    }
    return t('settings.boats.formatEmpty', 'Empty');
  };

  return (
    <div className="polar-visual-editor">
      <div className="polar-editor-toolbar">
        <div className="polar-editor-tabs" role="tablist" aria-label={t('settings.boats.editorMode', 'Polar editor mode')}>
          <button
            type="button"
            className={showRaw ? '' : 'active'}
            onClick={() => setShowRaw(false)}
            disabled={disabled}
          >
            <IconEye size={16} stroke={1.8} aria-hidden="true" />
            {t('settings.boats.visualEditor', 'Visual editor')}
          </button>
          <button type="button" className={showRaw ? 'active' : ''} onClick={() => setShowRaw(true)}>
            <IconCode size={16} stroke={1.8} aria-hidden="true" />
            {t('settings.boats.rawEditor', 'Raw')}
          </button>
        </div>
        <div className="polar-editor-stats" aria-label={t('settings.boats.polarSummary', 'Polar summary')}>
          <span>{t('settings.boats.points', '{{count}} points', { count: summary.pointCount })}</span>
          <span>{t('settings.boats.twsColumns', '{{count}} TWS columns', { count: summary.twsCount })}</span>
          <span>{t('settings.boats.twaRows', '{{count}} TWA angles', { count: summary.twaCount })}</span>
        </div>
      </div>

      {showRaw && (
        <div className="polar-transformer" aria-label={t('settings.boats.transformerTitle', 'Polar format transformer')}>
          <div className="polar-transformer-current">
            <span>{t('settings.boats.currentFormat', 'Current format')}</span>
            <strong>{formatLabel(currentFormat)}</strong>
          </div>
          <label className="polar-transformer-select">
            <span>{t('settings.boats.targetFormat', 'Target format')}</span>
            <select
              value={targetFormat}
              onChange={(event) => setTargetFormat(event.target.value as PolarExportFormat)}
              disabled={disabled}
            >
              <option value={CERTIFIED_JSON_FORMAT}>{t('settings.boats.formatJson', 'Certified JSON')}</option>
              <option value={TABLE_SEMICOLON_FORMAT}>{t('settings.boats.formatSemicolonTable', 'Semicolon table')}</option>
              <option value={TABLE_SPACE_FORMAT}>{t('settings.boats.formatSpaceTable', 'Space table')}</option>
            </select>
          </label>
          <button type="button" className="polar-transformer-button" onClick={transformPolars} disabled={!canTransform}>
            <IconArrowsExchange size={16} stroke={1.9} aria-hidden="true" />
            {t('settings.boats.transformFormat', 'Transform')}
          </button>
        </div>
      )}

      {!document.valid && (
        <div className="polar-editor-warning" role="alert">
          {t('settings.boats.polarsInvalid', document.message)}
        </div>
      )}

      {showRaw ? (
        <textarea
          className="polar-raw-textarea"
          value={value}
          onChange={updateRaw}
          disabled={disabled}
          spellCheck={false}
          rows={16}
          aria-label={t('settings.boats.rawPolarsLabel', 'Raw polar data')}
        />
      ) : (
        <div className="polar-editor-grid">
          <div className="polar-chart-card">
            <div className="polar-chart-head">
              <div>
                <span>{t('settings.boats.chartTitle', 'Polar curve')}</span>
                <strong>{document.format === 'certified-json' ? 'JSON' : document.format === 'table' ? 'Table' : '-'}</strong>
              </div>
              <div className="polar-tws-chips" aria-label={t('settings.boats.twsFilter', 'TWS filter')}>
                {twsValues.map((tws) => (
                  <button
                    key={tws}
                    type="button"
                    className={activeChartTws.includes(tws) ? 'active' : ''}
                    aria-pressed={activeChartTws.includes(tws)}
                    onClick={() => toggleChartTws(tws)}
                  >
                    {formatNumberInput(tws)}
                  </button>
                ))}
              </div>
            </div>
            <div className="polar-chart-canvas">
              {chartRows.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={chartRows} outerRadius="78%">
                    <PolarGrid />
                    <PolarAngleAxis dataKey="angle" tick={{ fill: 'var(--contrast)', fontSize: 11 }} />
                    <PolarRadiusAxis tick={{ fill: 'var(--textMuted)', fontSize: 10 }} />
                    <Tooltip />
                    {activeChartTws.map((tws) => {
                      const index = twsValues.indexOf(tws);
                      return (
                      <Radar
                        key={tws}
                        dataKey={makeSeriesKey(tws)}
                        name={`${formatNumberInput(tws)} TWS`}
                        stroke={chartColors[index % chartColors.length]}
                        fill={chartColors[index % chartColors.length]}
                        fillOpacity={visibleTws === tws ? 0.18 : 0.04}
                        strokeWidth={visibleTws === tws ? 2.4 : 1.2}
                        dot={visibleTws === tws}
                        connectNulls
                      />
                    );
                    })}
                  </RadarChart>
                </ResponsiveContainer>
              ) : (
                <div className="polar-chart-empty">{t('settings.boats.noPolarPoints', 'No polar points')}</div>
              )}
            </div>
          </div>

          <div className="polar-table-card">
            <div className="polar-table-head">
              <span>
                <IconTable size={16} stroke={1.8} aria-hidden="true" />
                {activeChartTws.length <= 1 && visibleTws !== null
                  ? t('settings.boats.pointTableForTws', 'TWS {{tws}} points', { tws: formatNumberInput(visibleTws) })
                  : t('settings.boats.selectedTwsPoints', '{{count}} selected TWS points', {
                    count: activeChartTws.length,
                  })}
              </span>
              <button type="button" onClick={addPoint} disabled={disabled}>
                <IconCirclePlus size={16} stroke={1.9} aria-hidden="true" />
                {t('settings.boats.addPoint', 'Add point')}
              </button>
            </div>
            <div className="polar-point-table-wrap">
              <table className="polar-point-table">
                <thead>
                  <tr>
                    <th>{t('settings.boats.tws', 'TWS')}</th>
                    <th>{t('settings.boats.twa', 'TWA')}</th>
                    <th>{t('settings.boats.boatSpeed', 'BoatSpeed')}</th>
                    <th aria-label={t('settings.boats.actions', 'Actions')} />
                  </tr>
                </thead>
                <tbody>
                  {visiblePoints.map((point) => (
                    <tr key={point.id}>
                      <td>
                        <input
                          type="text"
                          inputMode="decimal"
                          step="0.1"
                          value={pointDrafts[pointDraftKey(point.id, 'tws')] ?? formatNumberInput(point.tws)}
                          onChange={(event) => updatePointDraft(point.id, 'tws', event.target.value)}
                          onBlur={(event) => finishPointEdit(point, 'tws', event.target.value)}
                          onKeyDown={finishEditOnEnter}
                          disabled={disabled}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          inputMode="decimal"
                          step="0.1"
                          value={pointDrafts[pointDraftKey(point.id, 'twa')] ?? formatNumberInput(point.twa)}
                          onChange={(event) => updatePointDraft(point.id, 'twa', event.target.value)}
                          onBlur={(event) => finishPointEdit(point, 'twa', event.target.value)}
                          onKeyDown={finishEditOnEnter}
                          disabled={disabled}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          inputMode="decimal"
                          step="0.01"
                          value={pointDrafts[pointDraftKey(point.id, 'speed')] ?? formatNumberInput(point.speed)}
                          onChange={(event) => updatePointDraft(point.id, 'speed', event.target.value)}
                          onBlur={(event) => finishPointEdit(point, 'speed', event.target.value)}
                          onKeyDown={finishEditOnEnter}
                          disabled={disabled}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="polar-delete-button"
                          onPointerDown={(event) => event.preventDefault()}
                          onClick={() => removePoint(point.id)}
                          disabled={disabled}
                          aria-label={t('settings.boats.deletePoint', 'Delete point')}
                        >
                          <IconTrash size={16} stroke={1.8} aria-hidden="true" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
